export type HomeV2Daypart =
  | 'sunrise'
  | 'late_morning'
  | 'morning'
  | 'afternoon'
  | 'sunset'
  | 'evening'
  | 'night'

export type SectionBackgroundTab = 'play' | 'parks' | 'picks' | 'profile'

export type SectionDaypartBucket = 'morning' | 'afternoon' | 'night'

export type V2PhotoBackgroundTab = 'home' | SectionBackgroundTab

/** Map local hour to one of the seven V2 Home background assets. */
export function getHomeV2Daypart(hour: number): HomeV2Daypart {
  if (hour >= 5 && hour < 7) return 'sunrise'
  if (hour >= 7 && hour < 10) return 'late_morning'
  if (hour >= 10 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 17) return 'afternoon'
  if (hour >= 17 && hour < 20) return 'sunset'
  if (hour >= 20 && hour < 22) return 'evening'
  return 'night'
}

/** Collapse Home dayparts into morning / afternoon / night for tab backgrounds. */
export function getSectionDaypartBucket(hour: number): SectionDaypartBucket {
  const daypart = getHomeV2Daypart(hour)
  if (daypart === 'afternoon') return 'afternoon'
  if (daypart === 'sunrise' || daypart === 'late_morning' || daypart === 'morning') {
    return 'morning'
  }
  return 'night'
}

/** Home — seven daypart WebP assets (unchanged mapping, WebP format). */
export function getHomeV2BackgroundPath(hour: number): string {
  return `/backgrounds/home/home_${getHomeV2Daypart(hour)}.webp`
}

const SECTION_BACKGROUND_DIR: Record<SectionBackgroundTab, string> = {
  play: 'Play',
  parks: 'Parks',
  picks: 'Picks',
  profile: 'Profile',
}

/** Play / Parks / Picks / Profile — morning / afternoon / night WebP assets. */
export function getSectionBackgroundPath(tab: SectionBackgroundTab, hour: number): string {
  const bucket = getSectionDaypartBucket(hour)
  const dir = SECTION_BACKGROUND_DIR[tab]
  return `/backgrounds/${dir}/${tab}_${bucket}.webp`
}

export function getV2PhotoBackgroundPath(tab: V2PhotoBackgroundTab, hour: number): string {
  if (tab === 'home') return getHomeV2BackgroundPath(hour)
  return getSectionBackgroundPath(tab, hour)
}

export function isV2PhotoBackgroundTab(
  tab: string,
  playEnabled: boolean
): tab is V2PhotoBackgroundTab {
  if (tab === 'home' || tab === 'parks' || tab === 'picks' || tab === 'profile') return true
  return tab === 'play' && playEnabled
}

export type V2BackgroundDaypart = HomeV2Daypart | SectionDaypartBucket

/**
 * Parse section + daypart from a V2 background asset URL.
 * e.g. /backgrounds/home/home_night.webp → home, night
 *      /backgrounds/Play/play_morning.webp → play, morning
 */
export function parseV2BackgroundAssetFromSrc(src: string): {
  section: V2PhotoBackgroundTab
  daypart: V2BackgroundDaypart
} | null {
  const home = src.match(/\/backgrounds\/home\/home_(\w+)\.webp/i)
  if (home?.[1]) {
    return { section: 'home', daypart: home[1] as HomeV2Daypart }
  }
  const section = src.match(
    /\/backgrounds\/(?:Play|Parks|Picks|Profile)\/(play|parks|picks|profile)_(\w+)\.webp/i
  )
  if (section?.[1] && section[2]) {
    return {
      section: section[1].toLowerCase() as SectionBackgroundTab,
      daypart: section[2] as SectionDaypartBucket,
    }
  }
  return null
}

/**
 * Focal crop for fixed viewport backgrounds — object-fit: cover only, never stretch.
 * Tuned for mobile portrait (9:16 assets in ~9:19 viewport).
 * Lower vertical % shifts visible crop upward in the asset.
 */
const V2_BACKGROUND_OBJECT_POSITION: Record<string, string> = {
  // Home — centered unless composition needs adjustment
  home_sunrise: 'center center',
  home_late_morning: 'center center',
  home_morning: 'center center',
  home_afternoon: 'center center',
  home_sunset: 'center center',
  home_evening: 'center center',
  home_night: 'center center',

  // Play — lift crop off oversized foreground props (esp. night tennis balls).
  // morning: nudge right/down so the sunburst is less centered under the white logo.
  play_morning: '40% 30%',
  play_afternoon: 'center 26%',
  play_night: 'center 8%',

  parks_morning: 'center 36%',
  parks_afternoon: 'center 34%',
  parks_night: 'center 24%',

  picks_morning: 'center 36%',
  picks_afternoon: 'center 34%',
  picks_night: 'center 30%',

  profile_morning: 'center 38%',
  profile_afternoon: 'center 36%',
  profile_night: 'center 32%',
}

export function getV2BackgroundObjectPosition(
  section: V2PhotoBackgroundTab,
  daypart: V2BackgroundDaypart
): string {
  const key = `${section}_${daypart}`
  return V2_BACKGROUND_OBJECT_POSITION[key] ?? 'center center'
}

/** Resolve object-position from the active background asset path. */
export function getV2BackgroundObjectPositionFromSrc(src: string): string {
  const parsed = parseV2BackgroundAssetFromSrc(src)
  if (!parsed) return 'center center'
  return getV2BackgroundObjectPosition(parsed.section, parsed.daypart)
}
